import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Overview from "@/pages/Overview";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import Commands from "@/pages/Commands";
import Notifications from "@/pages/Notifications";
import AuditLogs from "@/pages/AuditLogs";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import ErrorBoundary from "@/components/ErrorBoundary";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return (
    <div className="min-h-screen grid place-items-center bg-app-bg text-app-muted font-mono-d text-xs tracking-widest">
      AUTHENTICATING...
    </div>
  );
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppToaster() {
  const { theme } = useTheme();
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <Toaster
      theme={theme}
      position={mobile ? "bottom-center" : "top-right"}
      toastOptions={{
        style: {
          background: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          color: "var(--app-text)",
          borderRadius: 2,
          marginBottom: mobile ? "calc(var(--mobile-dock-height, 4rem) + env(safe-area-inset-bottom, 0px) + 0.5rem)" : undefined,
        },
      }}
    />
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <AppToaster />
      <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Overview />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="accounts/:id" element={<AccountDetail />} />
              <Route path="reports" element={<Reports />} />
              <Route path="commands" element={<ProtectedRoute roles={["admin", "manager"]}><Commands /></ProtectedRoute>} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="audit" element={<ProtectedRoute roles={["admin", "manager"]}><AuditLogs /></ProtectedRoute>} />
              <Route path="admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <AppRoutes />
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
